import { useState } from "react";

import type { CreatePromptPayload, Prompt } from "../types/api";

interface PromptFormProps {
  initial?: Prompt | null;
  onSubmit: (data: CreatePromptPayload) => Promise<void>;
  onCancel: () => void;
}

export function PromptForm({ initial, onSubmit, onCancel }: PromptFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState(initial?.type ?? "new_email");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [systemPrompt, setSystemPrompt] = useState(initial?.systemPrompt ?? "");
  const [userPromptTemplate, setUserPromptTemplate] = useState(initial?.userPromptTemplate ?? "");
  const [thinkingInstructions, setThinkingInstructions] = useState(
    initial?.thinkingInstructions ?? ""
  );
  const [minTrafficShare, setMinTrafficShare] = useState(initial?.minTrafficShare ?? 0.005);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name,
        type,
        description,
        systemPrompt,
        userPromptTemplate,
        thinkingInstructions: thinkingInstructions || null,
        minTrafficShare,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 max-w-3xl"
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="prompt-name" className="block text-sm font-medium text-slate-700 mb-2">
            Name
          </label>
          <input
            id="prompt-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="prompt-name"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="prompt-type" className="block text-sm font-medium text-slate-700 mb-2">
            Type
          </label>
          <select
            id="prompt-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="prompt-type"
            disabled={!!initial}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500"
          >
            <option value="new_email">new_email</option>
            <option value="reply">reply</option>
            <option value="thinking">thinking</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="prompt-description"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            Description
          </label>
          <textarea
            id="prompt-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="prompt-description"
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label htmlFor="prompt-system" className="block text-sm font-medium text-slate-700 mb-2">
            System Prompt
          </label>
          <textarea
            id="prompt-system"
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={6}
            data-testid="prompt-system"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label
            htmlFor="prompt-user-template"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            User Prompt Template
          </label>
          <textarea
            id="prompt-user-template"
            value={userPromptTemplate}
            onChange={(e) => setUserPromptTemplate(e.target.value)}
            rows={6}
            data-testid="prompt-user-template"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label
            htmlFor="prompt-thinking"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            Thinking Instructions
          </label>
          <textarea
            id="prompt-thinking"
            value={thinkingInstructions}
            onChange={(e) => setThinkingInstructions(e.target.value)}
            rows={4}
            data-testid="prompt-thinking"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label
            htmlFor="prompt-traffic-share"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            Min Traffic Share
          </label>
          <input
            id="prompt-traffic-share"
            type="number"
            step="0.001"
            value={minTrafficShare}
            onChange={(e) => setMinTrafficShare(Number(e.target.value))}
            data-testid="prompt-traffic-share"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">{error}</div>
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-slate-200">
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Saving..." : initial ? "Update Prompt" : "Create Prompt"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}
